import { useCallback, useEffect, useRef, useState } from "react";
import type { AppEvent } from "../api";
import { isElectronDesktop, syncEventToDesktop } from "../desktop";
import { getDesktopEventsHub } from "./desktopEventsHub";

export type WsStatus = "connecting" | "open" | "reconnecting" | "closed";

export type LiveEventSource = "live" | "catchup";

interface UseLiveEventsOptions {
  enabled: boolean;
  onEvent?: (event: AppEvent, source: LiveEventSource) => void;
  onAssignment?: (info: {
    resourceKey: string;
    message: string;
    assignedKeys: string[];
    events: AppEvent[];
  }) => void;
  onUnassignment?: (info: {
    resourceKey: string;
    message: string;
    assignedKeys: string[];
  }) => void;
}

function useDesktopLocalEvents({
  enabled,
  onEvent,
  onAssignment,
  onUnassignment,
}: UseLiveEventsOptions) {
  const [status, setStatus] = useState<WsStatus>("closed");
  const [events, setEvents] = useState<AppEvent[]>([]);
  const [error, setError] = useState<string | null>(null);
  const lastIdRef = useRef(0);
  const onEventRef = useRef(onEvent);
  const onAssignmentRef = useRef(onAssignment);
  const onUnassignmentRef = useRef(onUnassignment);
  onEventRef.current = onEvent;
  onAssignmentRef.current = onAssignment;
  onUnassignmentRef.current = onUnassignment;

  useEffect(() => {
    if (!enabled) {
      setStatus("closed");
      return;
    }

    const hub = getDesktopEventsHub();
    setEvents(hub.getSnapshot());

    const unEvents = hub.subscribe((rows) => {
      setEvents(rows);
      for (const e of rows) {
        lastIdRef.current = Math.max(lastIdRef.current, e.id);
      }
    });
    const unStatus = hub.subscribeStatus(({ status: st, error: err }) => {
      setStatus(st);
      setError(err);
    });
    const unAssign = hub.subscribeAssignment((msg) => {
      if (msg.type === "assignment") {
        onAssignmentRef.current?.({
          resourceKey: msg.resourceKey,
          message: msg.message,
          assignedKeys: msg.assignedKeys,
          events: msg.events || [],
        });
      } else {
        onUnassignmentRef.current?.({
          resourceKey: msg.resourceKey,
          message: msg.message,
          assignedKeys: msg.assignedKeys,
        });
      }
    });
    const unDiff = hub.subscribeDiffs((changed, source) => {
      for (const e of changed) {
        lastIdRef.current = Math.max(lastIdRef.current, e.id);
        onEventRef.current?.(e, source);
      }
    });

    return () => {
      unEvents();
      unStatus();
      unAssign();
      unDiff();
    };
  }, [enabled]);

  const clear = useCallback(() => setEvents([]), []);

  return { status, events, error, clear, lastEventId: lastIdRef };
}

export function useLiveEvents(opts: UseLiveEventsOptions) {
  const desktop = isElectronDesktop();
  const local = useDesktopLocalEvents({
    ...opts,
    enabled: opts.enabled && desktop,
  });
  const remote = useRemoteLiveEvents({
    ...opts,
    enabled: opts.enabled && !desktop,
  });
  return desktop ? local : remote;
}

function useRemoteLiveEvents({
  enabled,
  onEvent,
  onAssignment,
  onUnassignment,
}: UseLiveEventsOptions) {
  const [status, setStatus] = useState<WsStatus>("closed");
  const [events, setEvents] = useState<AppEvent[]>([]);
  const [error, setError] = useState<string | null>(null);
  const lastIdRef = useRef(0);
  const onEventRef = useRef(onEvent);
  const onAssignmentRef = useRef(onAssignment);
  const onUnassignmentRef = useRef(onUnassignment);
  onEventRef.current = onEvent;
  onAssignmentRef.current = onAssignment;
  onUnassignmentRef.current = onUnassignment;
  const wsRef = useRef<WebSocket | null>(null);
  const attemptRef = useRef(0);
  const stoppedRef = useRef(false);

  const upsertEvents = useCallback(
    (incoming: AppEvent[], source: LiveEventSource) => {
      if (!incoming.length) return;
      for (const e of incoming) {
        lastIdRef.current = Math.max(lastIdRef.current, e.id);
        onEventRef.current?.(e, source);
        void syncEventToDesktop(e);
      }
      setEvents((prev) => {
        const map = new Map(prev.map((e) => [e.id, e]));
        for (const e of incoming) {
          map.set(e.id, e);
        }
        const all = [...map.values()].sort((a, b) => a.id - b.id);
        const newest = all.slice(-500);
        const newestIds = new Set(newest.map((e) => e.id));
        const stockKeep = all.filter(
          (e) =>
            !newestIds.has(e.id) &&
            e.stockStage &&
            e.stockStage !== "none" &&
            !e.hidden
        );
        const extras = stockKeep.slice(-500);
        return [...extras, ...newest].sort((a, b) => a.id - b.id);
      });
    },
    []
  );

  const removeResource = useCallback((resourceKey: string) => {
    setEvents((prev) => prev.filter((e) => e.resourceKey !== resourceKey));
  }, []);

  useEffect(() => {
    if (!enabled) {
      stoppedRef.current = true;
      wsRef.current?.close();
      wsRef.current = null;
      setStatus("closed");
      return;
    }

    stoppedRef.current = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let pingTimer: ReturnType<typeof setInterval> | null = null;

    const connect = () => {
      if (stoppedRef.current) return;
      setStatus(attemptRef.current === 0 ? "connecting" : "reconnecting");
      setError(null);

      const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
      const ws = new WebSocket(`${proto}//${window.location.host}/ws`);
      wsRef.current = ws;

      ws.onopen = () => {
        attemptRef.current = 0;
        setStatus("open");
        ws.send(JSON.stringify({ type: "resume", afterId: lastIdRef.current }));
        pingTimer = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: "ping" }));
          }
        }, 20_000);
      };

      ws.onmessage = (ev) => {
        let msg: Record<string, unknown>;
        try {
          msg = JSON.parse(String(ev.data)) as Record<string, unknown>;
        } catch {
          return;
        }

        if ((msg.type === "event" || msg.type === "event_update") && msg.event) {
          const event = msg.event as AppEvent;
          if (event.hidden) {
            setEvents((prev) => prev.filter((e) => e.id !== event.id));
            return;
          }
          upsertEvents([event], "live");
        } else if (msg.type === "event_hidden") {
          const eventId = typeof msg.eventId === "number" ? msg.eventId : null;
          if (eventId != null) {
            setEvents((prev) => prev.filter((e) => e.id !== eventId));
          }
        } else if (msg.type === "catchup" && Array.isArray(msg.events)) {
          upsertEvents(
            (msg.events as AppEvent[]).filter((e) => !e.hidden),
            "catchup"
          );
        } else if (msg.type === "assignment") {
          const events = Array.isArray(msg.events) ? (msg.events as AppEvent[]) : [];
          upsertEvents(events, "live");
          onAssignmentRef.current?.({
            resourceKey: String(msg.resourceKey ?? ""),
            message: String(msg.message ?? "New assignment"),
            assignedKeys: Array.isArray(msg.assignedKeys)
              ? (msg.assignedKeys as string[])
              : [],
            events,
          });
        } else if (msg.type === "unassignment") {
          const eventId = typeof msg.eventId === "number" ? msg.eventId : null;
          const key = String(msg.resourceKey ?? "");
          if (eventId != null) {
            setEvents((prev) => prev.filter((e) => e.id !== eventId));
          } else if (key) {
            removeResource(key);
          }
          onUnassignmentRef.current?.({
            resourceKey: key,
            message: String(msg.message ?? "Assignment removed"),
            assignedKeys: Array.isArray(msg.assignedKeys)
              ? (msg.assignedKeys as string[])
              : [],
          });
        } else if (msg.type === "error") {
          setError(String(msg.error ?? "WebSocket error"));
        }
      };

      ws.onclose = (ev) => {
        if (pingTimer) clearInterval(pingTimer);
        wsRef.current = null;
        if (stoppedRef.current) {
          setStatus("closed");
          return;
        }
        if (ev.code === 4401) {
          setStatus("closed");
          setError("Session expired — please sign in again");
          return;
        }
        const delay = Math.min(30_000, 1000 * 2 ** attemptRef.current + Math.random() * 400);
        attemptRef.current += 1;
        setStatus("reconnecting");
        reconnectTimer = setTimeout(connect, delay);
      };

      ws.onerror = () => {
        // onclose handles reconnect
      };
    };

    connect();

    const onOnline = () => {
      if (stoppedRef.current) return;
      if (!wsRef.current || wsRef.current.readyState === WebSocket.CLOSED) {
        attemptRef.current = 0;
        connect();
      }
    };
    window.addEventListener("online", onOnline);

    return () => {
      stoppedRef.current = true;
      window.removeEventListener("online", onOnline);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (pingTimer) clearInterval(pingTimer);
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [enabled, upsertEvents, removeResource]);

  const clear = useCallback(() => setEvents([]), []);

  return { status, events, error, clear, lastEventId: lastIdRef };
}
